/* Native details, standard links and the Shopify customer form work without this enhancement. */
(() => {
  if (window.__franklordChromeLoaded) return;
  window.__franklordChromeLoaded = true;
  const initialized = new WeakMap();
  const initializeScrollHeader = (root, signal) => {
    if (!root.matches('[data-flc-header]')) return;
    // A measured spacer preserves the page position. Fixed positioning also works
    // inside Kalles' overflow-clipped website wrapper, where sticky does not.
    const spacer = document.createElement('div');
    spacer.className = 'flc-header-spacer';
    spacer.setAttribute('aria-hidden', 'true');
    root.before(spacer);
    let baseHeight = 0;
    const scrollPosition = () => Math.max(0, window.scrollY || document.scrollingElement?.scrollTop || document.documentElement.scrollTop || document.body?.scrollTop || 0);
    let previousY = scrollPosition();
    let travel = 0;
    let direction = 0;
    let scrollFrame = 0;
    let measureFrame = 0;
    let disposed = false;
    let keyboardNavigation = false;
    const alwaysVisible = root.dataset.flcScrollMode === 'always';
    const reveal = () => root.classList.remove('flc-header--hidden');
    const keyboardFocus = () => {
      const active = document.activeElement;
      return root.contains(active) && (keyboardNavigation || active?.matches?.(':focus-visible'));
    };
    const protectedInteraction = () => keyboardFocus() || root.querySelector('details[open]');
    const measure = () => {
      if (disposed) return;
      const bounds = spacer.getBoundingClientRect();
      root.style.setProperty('--flc-header-left', `${bounds.left}px`);
      root.style.setProperty('--flc-header-width', `${bounds.width}px`);
      const mobileMenu = root.querySelector('[data-flc-mobile-menu]');
      const expandedHeight = mobileMenu?.open ? mobileMenu.getBoundingClientRect().height : 0;
      const inner = root.querySelector('.flc-header-inner');
      const innerHeight = inner?.getBoundingClientRect().height || 0;
      const minimumInnerHeight = inner ? parseFloat(window.getComputedStyle(inner).minHeight) || 0 : 0;
      // The mobile row has a minimum height; retain that space when its menu opens.
      baseHeight = Math.ceil(root.getBoundingClientRect().height - innerHeight + Math.max(minimumInnerHeight, innerHeight - expandedHeight));
      spacer.style.height = `${Math.max(0, baseHeight)}px`;
      if (scrollPosition() <= baseHeight || protectedInteraction()) reveal();
    };
    const scheduleMeasure = () => {
      if (measureFrame) return;
      measureFrame = requestAnimationFrame(() => { measureFrame = 0; measure(); });
    };
    measure();
    root.classList.add('flc-header--floating');
    // Measure again after fixed positioning, including font/viewport changes.
    measure();
    const updateScroll = () => {
      scrollFrame = 0;
      // Clamp Safari overscroll so bounce at either edge cannot reverse the header.
      const height = Math.max(document.documentElement.scrollHeight, document.body?.scrollHeight || 0, document.scrollingElement?.scrollHeight || 0);
      const maximum = Math.max(0, height - window.innerHeight);
      const y = Math.min(scrollPosition(), maximum);
      const delta = y - previousY;
      previousY = y;
      root.classList.toggle('flc-header--keyboard-focus', Boolean(keyboardFocus()));
      if (alwaysVisible || y <= baseHeight + 16 || protectedInteraction()) {
        reveal();
        travel = 0;
        direction = 0;
        return;
      }
      if (!delta) return;
      const nextDirection = delta > 0 ? 1 : -1;
      travel = nextDirection === direction ? travel + Math.abs(delta) : Math.abs(delta);
      direction = nextDirection;
      // Small movement does not make the navigation flicker during touch scrolling.
      if (direction > 0 && travel >= 18) root.classList.add('flc-header--hidden');
      if (direction < 0 && travel >= 8) reveal();
    };
    window.addEventListener('scroll', () => {
      if (!scrollFrame) scrollFrame = requestAnimationFrame(updateScroll);
    }, { passive: true, signal });
    window.addEventListener('resize', scheduleMeasure, { passive: true, signal });
    window.addEventListener('pageshow', () => {
      previousY = scrollPosition();
      travel = 0;
      direction = 0;
      reveal();
      scheduleMeasure();
    }, { signal });
    // Clicking a link or closing a menu can leave mouse/touch focus behind.
    // Only keyboard focus should pin the header while the customer scrolls.
    document.addEventListener('pointerdown', () => {
      keyboardNavigation = false;
      root.classList.remove('flc-header--keyboard-focus');
    }, { passive: true, signal });
    document.addEventListener('keydown', event => {
      if (event.key !== 'Tab') return;
      keyboardNavigation = true;
      if (root.contains(document.activeElement)) {
        root.classList.add('flc-header--keyboard-focus');
        reveal();
      }
    }, { signal });
    root.addEventListener('focusin', () => {
      reveal();
      root.classList.toggle('flc-header--keyboard-focus', Boolean(keyboardFocus()));
      travel = 0;
      direction = 0;
    }, { signal });
    root.addEventListener('focusout', () => {
      root.classList.remove('flc-header--keyboard-focus');
    }, { signal });
    root.addEventListener('toggle', () => {
      if (protectedInteraction()) reveal();
      scheduleMeasure();
    }, { capture: true, signal });
    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(scheduleMeasure) : null;
    observer?.observe(root);
    observer?.observe(spacer);
    signal.addEventListener('abort', () => {
      disposed = true;
      cancelAnimationFrame(scrollFrame);
      cancelAnimationFrame(measureFrame);
      observer?.disconnect();
      spacer.remove();
      root.classList.remove('flc-header--floating', 'flc-header--hidden', 'flc-header--keyboard-focus');
      root.style.removeProperty('--flc-header-left');
      root.style.removeProperty('--flc-header-width');
    }, { once: true });
  };
  const closeMenus = (root, except = null) => {
    root.querySelectorAll('details[open]').forEach(details => {
      if (details !== except && !details.contains(except)) details.open = false;
    });
  };
  const initialize = (scope = document) => {
    scope.querySelectorAll('[data-fl-chrome]').forEach(root => {
      if (initialized.has(root)) return;
      const controller = new AbortController();
      const { signal } = controller;
      initialized.set(root, controller);
      initializeScrollHeader(root, signal);
      root.addEventListener('keydown', event => {
        if (event.key !== 'Escape') return;
        const details = event.target.closest('details[open]');
        if (!details || !root.contains(details)) return;
        event.preventDefault();
        details.open = false;
        details.querySelector('summary')?.focus();
      }, { signal });
      root.addEventListener('click', event => {
        if (!event.target.closest('a[href]')) return;
        // This also closes the menu when a homepage anchor is selected.
        closeMenus(root);
      }, { signal });
      root.addEventListener('toggle', event => {
        const selected = event.target;
        if (!selected.matches('[data-flc-submenu]') || !selected.open) return;
        root.querySelectorAll('[data-flc-submenu][open]').forEach(details => {
          if (details !== selected && !details.contains(selected)) details.open = false;
        });
      }, { capture: true, signal });
      root.addEventListener('focusout', event => {
        const next = event.relatedTarget;
        // Native Tab order remains intact; a menu closes after focus leaves the header.
        if (next && !root.contains(next)) closeMenus(root);
      }, { signal });
      const feedback = root.querySelector('[data-flc-form-result]');
      if (feedback && location.hash.includes('FranklordNewsletter-')) feedback.focus();
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  else initialize();
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll('[data-fl-chrome]').forEach(root => {
      initialized.get(root)?.abort();
      initialized.delete(root);
    });
  });
  document.addEventListener('click', event => {
    document.querySelectorAll('[data-flc-header]').forEach(root => {
      if (!root.contains(event.target)) closeMenus(root);
    });
  });
  const desktop = window.matchMedia('(min-width: 1150px)');
  desktop.addEventListener?.('change', () => {
    document.querySelectorAll('[data-flc-header]').forEach(root => closeMenus(root));
  });
  const updateCart = cart => {
    const rawCount = cart?.item_count ?? cart?.count;
    if (rawCount === null || rawCount === undefined || rawCount === '') return;
    const itemCount = Number(rawCount);
    if (!Number.isInteger(itemCount) || itemCount < 0) return;
    document.querySelectorAll('[data-flc-cart-count]').forEach(count => { count.textContent = String(itemCount); });
    document.querySelectorAll('[data-flc-cart-link]').forEach(link => {
      link.setAttribute('aria-label', `Shopping bag: ${itemCount} ${itemCount === 1 ? 'item' : 'items'}`);
    });
  };
  document.addEventListener('cart:updated', event => updateCart(event.detail?.cart || event.detail));
  // Refresh the count when the browser restores an older page from its back/forward cache.
  window.addEventListener('pageshow', event => {
    if (!event.persisted || !document.querySelector('[data-flc-cart-count]')) return;
    const root = window.Shopify?.routes?.root || '/';
    fetch(`${root}cart.js`, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(response => response.ok ? response.json() : null).then(updateCart).catch(() => {});
  });
})();
